import React, { FC, MouseEventHandler, ReactNode } from 'react'
import styled from 'styled-components'
import { noop } from 'lodash'

import { Tooltip } from '../../Tooltip'
import {
  ButtonRole,
  ButtonEmphasis,
  ButtonShape,
  ButtonSize,
  ButtonModifiers,
  ButtonMinMaxSizes
} from './types'
import { getButtonStyles } from './styles/getButtonStyles'
import { Loader } from '../../../Loader'
import { IconName } from '../Icon/iconDef'
import SvgIcon from '../Icon/SvgIcon'

export type Props = {
  role?: ButtonRole
  emphasis?: ButtonEmphasis
  shape?: ButtonShape
  size?: ButtonSize
  icon?: IconName
  label?: ReactNode
  disabled?: boolean
  minWidth?: number
  onClick?: (event: React.MouseEvent<HTMLElement>) => void
  onClickWhenDisabled?: (event: React.MouseEvent<HTMLElement>) => void
  minMaxSizes?: ButtonMinMaxSizes
  tooltip?: string | JSX.Element | false | (string | JSX.Element)[]
  loading?: boolean
  type?: 'submit' | 'reset' | 'button' | undefined
  className?: string
  modifiers?: ButtonModifiers
  stopClickPropagation?: boolean
  'data-test'?: string
}

export const Button: FC<Props> = ({
  type = 'button',
  tooltip,
  label,
  onClick = noop,
  onClickWhenDisabled = noop,
  role = 'neutral',
  size = 'large',
  shape = 'rectangle',
  emphasis = 'secondary',
  loading = false,
  disabled = false,
  minMaxSizes = {},
  icon,
  className,
  modifiers = {},
  stopClickPropagation = type !== 'submit',
  'data-test': dataTest,
  ...rest
}) => {
  const handleClick: MouseEventHandler = e => {
    if (stopClickPropagation) {
      e.stopPropagation()
    }
    return !disabled ? onClick(e) : onClickWhenDisabled(e)
  }

  return (
    <Tooltip title={tooltip}>
      <StyledButtonElement
        type={type}
        data-test={dataTest}
        onClick={handleClick}
        $disabled={disabled}
        $shape={shape}
        $size={size}
        $role={role}
        $emphasis={emphasis}
        $modifiers={modifiers}
        className={className}
        $minMaxSizes={minMaxSizes}
        {...rest}
      >
        {loading && <Loader size="small" />}

        {!loading && icon && <SvgIcon name={icon} />}
        <Label $hidden={loading}>{label}</Label>
      </StyledButtonElement>
    </Tooltip>
  )
}

const StyledButtonElement = styled.button<{
  $size: ButtonSize
  $shape: ButtonShape
  $disabled: boolean
  $emphasis: ButtonEmphasis
  $role: ButtonRole
  $modifiers: ButtonModifiers
  $minMaxSizes: ButtonMinMaxSizes
}>(({ $role, $size, $shape, $disabled, $emphasis, $modifiers, $minMaxSizes }) => [
  getButtonStyles(
    $shape,
    $emphasis,
    $size,
    $role,
    $minMaxSizes,
    $disabled,
    !!$modifiers.disableFocusOnClick
  )
])

const Label = styled.span<{ $hidden: boolean }>`
  visibility: ${props => props.$hidden && 'hidden'};
`

export type ButtonProps = Props
